import * as Sentry from "@sentry/react";

const RELOADED_AT_KEY = "lime:chunk-reload-at";

// Long enough that a reload which fails the same way cannot loop, short enough
// that a later deploy in the same tab still gets its own recovery reload.
export const CHUNK_RELOAD_COOLDOWN_MS = 5 * 60 * 1000;

export type PreloadErrorEvent = {
  payload?: unknown;
  preventDefault: () => void;
};

export type ChunkReloadEnvironment = {
  storage: Pick<Storage, "getItem" | "setItem"> | null;
  now: () => number;
  reload: () => void;
  report: (details: { reloaded: boolean; message: string }) => void;
};

function readLastReload(storage: ChunkReloadEnvironment["storage"]) {
  try {
    const value = Number(storage?.getItem(RELOADED_AT_KEY));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

function recordReload(
  storage: ChunkReloadEnvironment["storage"],
  now: number,
): boolean {
  if (!storage) return false;
  try {
    storage.setItem(RELOADED_AT_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}

/**
 * Reloads once to pick up the new deploy's chunks. Without a working
 * sessionStorage it never reloads, because nothing could stop a loop.
 * Returns whether it reloaded.
 */
export function handlePreloadError(
  event: PreloadErrorEvent,
  env: ChunkReloadEnvironment,
): boolean {
  const now = env.now();
  const lastReload = readLastReload(env.storage);
  const message =
    event.payload instanceof Error
      ? event.payload.message
      : String(event.payload ?? "");

  const withinCooldown =
    lastReload !== null && now - lastReload < CHUNK_RELOAD_COOLDOWN_MS;
  const reloaded = !withinCooldown && recordReload(env.storage, now);

  env.report({ reloaded, message });

  if (!reloaded) return false;

  event.preventDefault();
  env.reload();
  return true;
}

function sessionStorageOrNull(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function registerPreloadErrorRecovery(): void {
  window.addEventListener("vite:preloadError", (event) => {
    handlePreloadError(event as Event & { payload?: unknown }, {
      storage: sessionStorageOrNull(),
      now: () => Date.now(),
      reload: () => {
        void Sentry.flush(2000).finally(() => window.location.reload());
      },
      report: ({ reloaded, message }) =>
        Sentry.captureMessage("Chunk preload failed", {
          level: "warning",
          tags: { chunkReload: reloaded ? "reloaded" : "suppressed" },
          extra: { message },
        }),
    });
  });
}
